/**
 * `toolTurnHandover.ts` — which tool results END the model's turn, and which of
 * those come with a fixed line to say in its place.
 *
 * Some tools hand the SPOKEN turn to something other than the model, so the
 * transport must NOT ask for a follow-up reply after posting the result. Both
 * transports need the same answer — `RealtimeSession` (which would otherwise
 * send `response.create`) and `ResponsesTurnBridge` (which would otherwise run
 * another round) — so the rule lives here rather than being written out twice.
 *
 * Five cases today, and they are the same shape:
 *
 *   - `present_options` — the learner answers with a button, and their picks
 *     come back as the next user message (Unity suppresses its follow-up too).
 *   - `go_to_workflow_step`, when the arrival's own on-start script is about to
 *     narrate — the author's lines are the narration for that step, and an
 *     acknowledgment either talks over them or, worse, is composed from the NEW
 *     step's freshly-pushed run-state bullets: "we're still on this step for a
 *     moment, the video hasn't finished…", spoken before the script has said a
 *     word. That was the reported bug.
 *   - `start_workflow`, on ANY success. The run takes the session over in the
 *     middle of the call — playback switches the role to the Guide before the
 *     result is posted — so a follow-up would be the Guide answering a call it
 *     never made, over the first step's own `say` (two `response.create`s on one
 *     socket, and the second is dropped), while on the Responses bridge the
 *     switch has already reset the conversation the follow-up would continue. A
 *     start parked on console placement ends the turn too: the placement slate
 *     says why it is up.
 *   - `stop_workflow`, on ANY success — and this one's reply is REPLACED, not
 *     merely silenced. The result carries a fixed confirmation in a `reply="…"`
 *     field ("Preview stopped" for an author, "Workflow stopped" for a learner
 *     who is taken out of the project), and the orchestrator speaks exactly that
 *     line in the model's place (user direction, AI-1357: "a standard response
 *     string always"). Composed by the model, the confirmation changed wording
 *     from call to call and trailed a sentence about the editor, which is what
 *     that asked to remove. The line rides `INTERNAL SAY:`, the contract that
 *     already delivers an author's words verbatim.
 *   - `control_media`, on ANY success (user direction, 2026-09-17: "give the
 *     control_media a narration field and make the move or the replay silent").
 *     Working the learner's own video is not news — they asked for it, they are
 *     looking at the panel, and its scrubber shows where the clip landed. Left
 *     to answer, the model reported the action back ("Playing it again now."),
 *     which is a preamble in everything but POSITION: `base.md`'s ban is worded
 *     against the pre-call slot while `:58`/`:67` require one utterance AFTER
 *     the work, and "the result" is what an action's outcome reads as. Closing
 *     the slot at the transport is what makes it stick — the neighbouring
 *     `go_to_workflow_step` is silent for the same reason, and a prompt line
 *     asking for silence would be fighting a `gpt-realtime-2.x` default.
 *
 * ⚠ The STEP-MOVE case is CONDITIONAL and must stay conditional. A step whose
 * on-start script says nothing — or that has no script at all — has nobody else
 * to narrate it, and suppressing there answers the learner's "next" with
 * silence. The executor decides (it is the half that can see the script) and
 * says so in the result; this module only reads the answer. The START case is
 * unconditional for the opposite reason: a run whose first step says nothing
 * gets no agent speech from the Play button either, and matching the button is
 * the whole contract of the tool.
 */

import { AgentToolNames } from "@ai/registry/agentToolNames";

/**
 * The `narration=` field `go_to_workflow_step` stamps on a successful move.
 *
 * A field on the existing `ok: workflow_step …` line rather than a side channel,
 * because it is worth saying to the MODEL as well as to the transport: it is
 * what tells the agent whether the step it just moved to narrates itself. One
 * constant, imported by the producer and by this reader, so the two cannot
 * drift into a silent no-op.
 */
export const K_NARRATION_SCRIPT = "narration=script";

/** The other half: nothing else will speak, so the model must narrate the step. */
export const K_NARRATION_AGENT = "narration=you";

/**
 * The third value, and the only one that means NOBODY speaks: stamped by
 * `control_media` on a successful command.
 *
 * Same field as the two above because it answers the same question — who has
 * the floor after this call — and the model reads it as well as the transport.
 * `none` is deliberately not `you`: the video itself is the outcome, so there
 * is no line to compose and the turn simply ends.
 *
 * ⚠ It is stamped on `ok:` only. Every `control_media` refusal returns an
 * `error:` line, which `toolHandedOverSpokenTurn` rejects on its first check —
 * so `media_not_ready`, `step_has_no_media` and the rest keep their voice.
 * Silence there would read as a hang, and they have no other surface.
 */
export const K_NARRATION_NONE = "narration=none";

/**
 * The field a tool stamps DIRECTLY after its status word when its outcome has
 * ONE fixed confirmation to speak: `ok: workflow_stopped reply="Preview stopped" …`.
 *
 * The producer and the reader share this function and the pattern below, the
 * way `narration=` shares its constants. The field sits right after the status
 * because the rest of a result is free text — a workflow or step name quoted
 * later in the line could contain anything, and the reader is anchored so that
 * it never matches inside one.
 */
export function formatToolSpokenReply(line: string): string {
  return ` reply="${line}"`;
}

const K_SPOKEN_REPLY_FIELD = /^ok: \S+ reply="([^"]+)"/;

/**
 * The exact line to SPEAK in place of the model's reply to this tool call, or
 * null when the model answers as usual. A non-null answer also ends the turn
 * ({@link toolHandedOverSpokenTurn}).
 *
 * Read only for the tools that stamp the field, so a `reply="` that happens to
 * appear in some other tool's output is never mistaken for one.
 */
export function toolSpokenReply(toolName: string, result: string): string | null {
  if (toolName !== AgentToolNames.StopWorkflow) return null;
  const line = K_SPOKEN_REPLY_FIELD.exec(result)?.[1]?.trim() ?? "";
  return line.length > 0 ? line : null;
}

/**
 * True when the tool call that just ran handed the spoken turn away, so the
 * caller must not ask the model to reply.
 *
 * `result` is matched rather than a separate flag because it is the channel
 * both transports already have — neither holds the executor, and neither may
 * know what a workflow is.
 */
export function toolHandedOverSpokenTurn(toolName: string, result: string): boolean {
  if (!result.startsWith("ok")) return false;
  if (toolName === AgentToolNames.PresentOptions) return true;
  if (toolName === AgentToolNames.GoToWorkflowStep) return result.includes(K_NARRATION_SCRIPT);
  if (toolName === AgentToolNames.ControlMedia) return result.includes(K_NARRATION_NONE);
  if (toolName === AgentToolNames.StartWorkflow) return true;
  if (toolSpokenReply(toolName, result) !== null) return true;
  return false;
}
